import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./SalaryInformation.module.sass";
import Item from "../Item";
import TextInput from "../../../components/TextInput";
import DualButton from "../../../components/DualButton";
import Dropdown from "../../../components/Dropdown";
import { checkSalary } from "../../../services/salary.service";

// Options pour l'année d'apprentissage
const STARTING_YEAR_OPTIONS = [
  "1ʳᵉ année",
  "2ᵉ année", 
  "3ᵉ année"
];

const SalaryInformation = ({ className, onSalaryDetailsChange, selectedStudent, contractDetails }) => {
  const [salaryYear1, setSalaryYear1] = useState('');
  const [hasConventionSalary, setHasConventionSalary] = useState('Non');
  const [conventionSalary, setConventionSalary] = useState('');
  const [smicPercentage, setSmicPercentage] = useState('');
  const [startingYear, setStartingYear] = useState(1);
  const [startingYearDropdown, setStartingYearDropdown] = useState("1ʳᵉ année");
  const [detailedSalaryPeriods, setDetailedSalaryPeriods] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const [missingDataInfo, setMissingDataInfo] = useState('');

  // Fonction pour gérer le changement d'année d'apprentissage
  const handleStartingYearChange = (selectedValue) => {
    setStartingYearDropdown(selectedValue);
    // Extraire le numéro de l'année depuis la valeur sélectionnée
    const yearNumber = parseInt(selectedValue.charAt(0)); // Premier caractère = numéro de l'année
    setStartingYear(yearNumber);
  };

  // Mettre à jour le parent à chaque changement
  useEffect(() => {
    if (typeof onSalaryDetailsChange === 'function') {
      onSalaryDetailsChange({
        hiring_salary: salaryYear1,
        has_convention_salary: hasConventionSalary,
        convention_salary: conventionSalary,
        smic_percentage: smicPercentage,
        annual_remuneration: buildAnnualRemunerationArray(detailedSalaryPeriods)
      });
    }
  }, [salaryYear1, hasConventionSalary, conventionSalary, smicPercentage, detailedSalaryPeriods]);

  // Fonction pour construire le tableau annual_remuneration
  const buildAnnualRemunerationArray = (detailedPeriods) => {
    if (!detailedPeriods || detailedPeriods.length === 0) return [];
    
    // Grouper les périodes par année pour calculer correctement les parties
    const periodsByYear = {};
    
    detailedPeriods.forEach(period => {
      if (!periodsByYear[period.annee]) {
        periodsByYear[period.annee] = [];
      }
      periodsByYear[period.annee].push(period);
    });
    
    // Construire le tableau avec les ordres corrects
    return detailedPeriods.map(period => {
      // Convertir les dates au format ISO
      const dateDebut = new Date(period.startDate).toISOString();
      const dateFin = new Date(period.endDate).toISOString();
      
      // Calculer la partie dans l'année (1, 2, 3...)
      const yearPeriods = periodsByYear[period.annee];
      const partieIndex = yearPeriods.findIndex(p => 
        p.startDate === period.startDate && p.endDate === period.endDate
      ) + 1;
      
      // Calculer l'ordre (année.partie)
      const ordre = `${period.annee}.${partieIndex}`;
      
      return {
        dateDebut,
        dateFin,
        taux: parseInt(period.percentage),
        typeSalaire: period.smicType || 'SMIC',
        ordre
      };
    });
  };

  // Calcul du pourcentage du SMIC lorsque le salaire change
  useEffect(() => {
    if (salaryYear1 && salaryYear1 > 0 && detailedSalaryPeriods.length > 0) {
      const firstPeriod = detailedSalaryPeriods[0];
      if (firstPeriod?.percentage) {
        setSmicPercentage(firstPeriod.percentage.toString());
      }
    }
  }, [salaryYear1, detailedSalaryPeriods]);

  // Effet pour calculer automatiquement le salaire
  useEffect(() => {
    setMissingDataInfo('');
    setCalculationError('');
    
    if (isCalculating) return;

    if (selectedStudent?.birth_date && contractDetails?.startDateOfContract && contractDetails?.endDateOfContract) {
      calculateSalary();
    } else {
      const missingData = [];
      if (!selectedStudent?.birth_date) missingData.push("date de naissance de l'étudiant");
      if (!contractDetails?.startDateOfContract) missingData.push("date de début de contrat");
      if (!contractDetails?.endDateOfContract) missingData.push("date de fin de contrat");

      if (missingData.length > 0) {
        setMissingDataInfo(`Impossible de calculer automatiquement le salaire. Données manquantes : ${missingData.join(', ')}.`);
      }
    }
  }, [selectedStudent?.birth_date, contractDetails?.startDateOfContract, contractDetails?.endDateOfContract, startingYear]);

  const calculateSalary = async () => {
    if (isCalculating) return;
    
    setIsCalculating(true);
    setCalculationError('');

    try {
      const result = await checkSalary({
        birthDate: selectedStudent.birth_date,
        startDate: contractDetails.startDateOfContract,
        endDate: contractDetails.endDateOfContract,
        startingYear
      });

      if (result.success && result.data?.periodes?.length > 0) {
        setDetailedSalaryPeriods(result.data.periodesDetaillees || []);
        setSalaryYear1(result.data.periodes[0].salary.toString());
      } else {
        setCalculationError(result.message || 'Erreur lors du calcul du salaire');
      }
    } catch (error) {
      console.error('Erreur lors du calcul du salaire:', error);
      setCalculationError('Erreur lors du calcul du salaire');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <Item
      className={cn(styles.card, className)}
      title="Rémunération"
      classTitle="title-purple"
    >
      <div className={styles.fieldset}>
        {missingDataInfo && (
          <div className={styles.warningMessage}>
            <p>{missingDataInfo}</p>
          </div>
        )}
        
        {calculationError && (
          <div className={styles.errorMessage}>
            <p>{calculationError}</p>
          </div>
        )}

     
        <div className={styles.field}>
          <Dropdown
            className={styles.input}
            label="Année d'apprentissage au démarrage"
            tooltip="Sélectionnez l'année d'apprentissage à partir de laquelle commencer le calcul du salaire"
            value={startingYearDropdown}
            setValue={handleStartingYearChange}
            options={STARTING_YEAR_OPTIONS}
          />
        </div>
  

        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              className={styles.input}
              label="Salaire d'embauche (€)"
              name="salary_year_1"
              type="number"
              step="0.01"
              placeholder="Ex: 1200.00"
              value={salaryYear1}
              onChange={(e) => setSalaryYear1(e.target.value)}
              tooltip="Salaire mensuel brut d'embauche"
            />
          </div>

          <div className={styles.field}>
            <TextInput
              className={styles.input}
              label="Pourcentage du SMIC (%)"
              name="smic_percentage"
              type="number"
              step="0.01"
              placeholder="Ex: 67.89"
              value={smicPercentage}
              onChange={(e) => setSmicPercentage(e.target.value)}
              tooltip="Pourcentage par rapport au SMIC en vigueur"
              disabled
            />
          </div>
        </div>



        {detailedSalaryPeriods.length > 0 && (
          <div className={styles.salaryEvolution}>
            <h4>Estimation de l'évolution du salaire</h4>
            <table className={styles.salaryTable}>
              <thead>
                <tr>
                  <th>Année</th>
                  <th>Période</th>
                  <th>Âge</th>
                  <th>% du SMIC</th>
                  <th>Salaire brut</th>
                </tr>
              </thead>
              <tbody>
                {detailedSalaryPeriods.map((period, index) => (
                  <tr key={index}>
                    <td>{period.annee}</td>
                    <td>{period.startDate} au {period.endDate}</td>
                    <td>{period.age} ans</td>
                    <td>{period.percentage}%</td>
                    <td>{period.salary} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Item>
  );
};

export default SalaryInformation;